import React, { useState } from "react"
import { styled } from "../stitches.config";
import { Formik, Form, Field } from "formik"
import * as Yup from "yup"
import { SwitchTransition, CSSTransition } from 'react-transition-group'
import {
    isValid,
    parse,
    addDays,
    differenceInCalendarMonths,
    getYear,
    getMonth
} from 'date-fns'

import Calendar from "../components/Calendar"

const FIELD_REQUIRED = "Pole wymagane"
const PERIOD_DURATION_ERROR = "Długość cyklu odbiega od normy"
const WRONG_DATE_ERROR = "Podaj właściwą datę"

const Calculator = () => {
    const [firstStep, setFirstStep] = useState(true);
    const [dateError, setDateError] = useState(false);
    const [answers, setAnswers] = useState({
        ovulation: null,
        highest: null,
        medium: null,
        lowest: null
    })
    const [isMonthDiff, setMonthDiff] = useState({
        prev: false,
        next: false
    })

    const testDate = values => {
        const { day, month, year } = values;
        return isValid(parse(`${year}-${month}-${day}`, 'yyyy-MM-dd', new Date()))
    }

    const handleResetForm = (formik) => {
        formik.resetForm()
        setFirstStep(true)
    }

    const handleSubmit = (values) => {
        const isDateCorrect = testDate(values)
        if (isDateCorrect) {
            const givenDate = new Date(values.year, values.month - 1, values.day)
            const targetDate = addDays(givenDate, values.period - 14)

            const lowestDate = addDays(targetDate, -4)
            const highestDate = addDays(targetDate, 2)

            const diffLowest = differenceInCalendarMonths(targetDate, lowestDate)
            const diffHighest = differenceInCalendarMonths(highestDate, targetDate)
            if (diffLowest === 1) setMonthDiff(prevState => ({ ...prevState, prev: true }))
            if (diffHighest === 1) setMonthDiff(prevState => ({ ...prevState, next: true }))

            setAnswers({
                ovulation: targetDate,
                highest: [addDays(targetDate, -1), targetDate],
                medium: [addDays(targetDate, -3), addDays(targetDate, -2), addDays(targetDate, 1)],
                lowest: [lowestDate, highestDate]
            })
            setFirstStep(false)
        } else {
            setDateError(true)
        }
    }

    return (
        <Wrap>
            <Formik
                initialValues={{
                    period: '',
                    day: '',
                    month: '',
                    year: ''
                }}
                validationSchema={Yup.object().shape({
                    period: Yup.number()
                        .min(23, PERIOD_DURATION_ERROR)
                        .max(35, PERIOD_DURATION_ERROR)
                        .required(FIELD_REQUIRED),
                    day: Yup.number()
                        // .min(1)
                        // .max(31)
                        .required(),
                    month: Yup.number()
                        // .min(1)
                        // .max(12)
                        .required(),
                    year: Yup.number()
                        // .min(2000)
                        // .max(new Date().getFullYear())
                        .required()
                })}
                onSubmit={values => handleSubmit(values)}>
                {formik => (
                    <SwitchTransition mode="out-in">
                        <CSSTransition
                            key={firstStep ? "First step" : "Final step"}
                            classNames='fade'
                            timeout={300}
                            mountOnEnter
                            unmountOnExit>
                            {firstStep ?
                                <FormWrapper>
                                    <Form>
                                        <p>Oblicz swoje dni płodne</p>
                                        <div>Podaj średnią długość cyklu</div>
                                        <Input type="number" name="period" width={60} placeholder="00" />
                                        <Error>{formik.touched.period && formik.errors.period ? formik.errors.period : " "}</Error>
                                        <div>Podaj datę pierwszego dnia ostatniej miesiączki</div>
                                        <InputGroup>
                                            <Input type="number" name="day" width={60} placeholder="DD" />
                                            <Input type="number" name="month" width={60} placeholder="MM" />
                                            <Input type="number" name="year" id='yearInput' width={100} placeholder="RRRR" />
                                        </InputGroup>
                                        <Error>{dateError ? WRONG_DATE_ERROR : ""}</Error>
                                        <Error>{(formik.touched.year && formik.errors.year) || (formik.touched.month && formik.errors.month) || (formik.touched.day && formik.errors.day) ? FIELD_REQUIRED : " "}</Error>
                                        <Button disabled={!(formik.isValid && formik.dirty)} type="submit">Oblicz</Button>
                                    </Form>
                                </FormWrapper>
                                :
                                <CalendarsWrap>
                                    <CalendarsTitle>Twój kalendarz dni płodnych</CalendarsTitle>
                                    {isMonthDiff.prev && <Calendar year={getYear(answers.lowest[0])} month={getMonth(answers.lowest[0])} answers={answers} />}
                                    <Calendar year={getYear(answers.ovulation)} month={getMonth(answers.ovulation)} answers={answers} />
                                    {isMonthDiff.next && <Calendar year={getYear(answers.lowest[1])} month={getMonth(answers.lowest[1])} answers={answers} />}
                                    <LegendWrap>
                                        <Legend>
                                            <p>Legenda:</p>
                                            <LegendItem color="FFB648"><span></span>- Owulacja</LegendItem>
                                            <LegendItem color="FF2E57"><span></span>- Największa szansa na zajście w ciążę</LegendItem>
                                            <LegendItem color="FF6180"><span></span>- Średnia szansa na zajście w ciążę</LegendItem>
                                            <LegendItem color="FF94A9"><span></span>- Mała szansa na zajście w ciążę</LegendItem>
                                            <LegendItem color="FFFFFF"><span></span>- Dni niepłodne</LegendItem>
                                        </Legend>
                                        <Button type="button" onClick={() => handleResetForm(formik)}>Oblicz ponownie</Button>
                                    </LegendWrap>
                                </CalendarsWrap>
                            }
                        </CSSTransition>
                    </SwitchTransition>
                )}
            </Formik>
        </Wrap>
    )
}

export default Calculator

const Error = styled('div', {
    color: '#cc0000 !important',
    fontStyle: 'italic! important',
    minHeight: '22px',
})

const CalendarsWrap = styled("div", {
    margin: 'auto',
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'center',
})

const Button = styled("button", {
    '&:disabled': {
        filter: 'saturate(50%)',
    }
})

const LegendItem = styled("p", {
    display: 'flex',
    margin: '0px',
    color: '$grayscale50',
    fontSize: '14px',
    fontStyle: 'italic',
    marginTop: '2px',
    width: 'auto',
    'span': {
        display: 'inline-block',
        width: '18px',
        height: '18px',
        marginRight: '4px',
        borderRadius: '50%',
    },
    variants: {
        color: {
            FFB648: { "& span": { backgroundColor: '#FFB648' } },
            FF2E57: { "& span": { backgroundColor: '#FF2E57' } },
            FF6180: { "& span": { backgroundColor: '#FF6180' } },
            FF94A9: { "& span": { backgroundColor: '#FF94A9' } },
            FFFFFF: {
                "& span": {
                    backgroundColor: '#FFFFFF',
                    border: "0.5px solid #777",
                }
            },
        },
    },
})

const CalendarsTitle = styled("p", {
    fontSize: '26px',
    fontWeight: 'bold',
    textTransform: 'uppercase',
    textAlign: 'center',
    width: '100%',
    '@bp4': {
        fontSize: '28px',
    }
})

const LegendWrap = styled("div", {
    paddingTop: '10px',
    margin: '30px auto',
    display: 'flex',
    flexDirection: 'column',
    '@bp4': {
        margin: '30px 20%',
    },
    p: {
        margin: '5px 0px',
        textAlign: 'left',
        '&:nth-of-type(1)': {
            fontStyle: 'normal !important',
        }
    },
    button: {
        width: '100%',
        height: '50px',
        border: '2px solid $mamaduPrimary',
        borderBottomLeftRadius: '4px',
        borderBottomRightRadius: '4px',
        color: '$grayscale100',
        backgroundColor: '$mamaduPrimary',
        textTransform: 'uppercase',
        fontSize: '18px',
    }
})

const Legend = styled("div", {
    border: `2px solid $mamaduPrimary`,
    borderTopLeftRadius: '4px',
    borderTopRightRadius: '4px',
    padding: '10px',
})

const InputGroup = styled("div", {
    display: 'flex',
    flexDirection: 'row',
})

const Input = styled(Field, {
    display: 'block',
    padding: '10px',
    color: '$grayscale100',
    fontSize: '24px',
    textAlign: 'center',
    width: '60px',
    background: '$grayscale0',
    marginRight: '7px',
    border: '2px solid $mamaduPrimary',
    borderRadius: '5px',
    '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
        WebkitAppearance: 'none',
        margin: '0',
    },
    '&#yearInput': {
        width: '100px',
    },
    MozAppearance: 'textfield',
})

const Wrap = styled("div", {
    width: '400px',
    maxWidth: '100%',
    minHeight: '480px',
    display: 'flex',
    margin: '60px auto',
    padding: '0 20px',
    border: '2px solid $mamaduPrimary',
    borderRadius: '5px',
    alignItems: 'center',
    justifyItems: 'center',
    textAlign: 'center',
    flexDirection: 'column',
    color: `$mamaduPrimary`,
    '.fade-enter': {
        opacity: '0',
    },
    '.fade-exit': {
        opacity: '1',
    },
    '.fade-enter-active': {
        opacity: '1',
    },
    '.fade-exit-active': {
        opacity: '0',
    },
    '.fade-enter-active, .fade-exit-active': {
        transition: 'opacity 500ms',
    },
    '@bp4': {
        width: '60vw',
    }
})

const FormWrapper = styled("div", {
    'form': {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
    },
    'p': {
        fontSize: '26px',
        fontWeight: 'bold',
        textTransform: 'uppercase',
        textAlign: 'center',
        '@bp4': {
            fontSize: '28px',
        }
    },
    'div': {
        color: '$grayscale100',
        margin: '5px 0px',
    },
    'button': {
        margin: '30px 0px',
        border: '2px solid $mamaduPrimary',
        borderRadius: '5px',
        color: '$grayscale100',
        background: '$mamaduPrimary',
        padding: '10px 40px',
        textTransform: 'uppercase',
        fontSize: '18px',
    }
})
